<template>
  <div class="client-journey-empty">
    <h3 class="client-journey-empty__title">{{ title }}</h3>
    <p class="client-journey-empty__msg">{{ message }}</p>

    <ol class="client-journey-empty__timeline" aria-label="Étapes de livraison">
      <li
        v-for="(step, index) in steps"
        :key="step.id"
        class="client-journey-empty__step"
        :class="{
          'is-done': index < activeStep,
          'is-current': index === activeStep,
          'is-upcoming': index > activeStep,
        }"
      >
        <span class="client-journey-empty__marker" aria-hidden="true">
          {{ index < activeStep ? '✓' : index + 1 }}
        </span>
        <span class="client-journey-empty__step-body">
          <span class="client-journey-empty__step-label">{{ step.label }}</span>
          <span v-if="step.hint" class="client-journey-empty__step-hint">{{ step.hint }}</span>
        </span>
      </li>
    </ol>

    <div class="client-journey-empty__actions">
      <router-link
        v-if="primaryTo && primaryLabel"
        class="client-journey-empty__btn client-journey-empty__btn--primary"
        :to="primaryTo"
      >
        {{ primaryLabel }}
      </router-link>
      <router-link
        v-if="secondaryTo && secondaryLabel"
        class="client-journey-empty__btn client-journey-empty__btn--ghost"
        :to="secondaryTo"
      >
        {{ secondaryLabel }}
      </router-link>
    </div>
  </div>
</template>

<script>
const DEFAULT_STEPS = [
  { id: 'form', label: 'Questionnaire patrimonial', hint: 'Profil, situation, finances, objectifs' },
  { id: 'submit', label: 'Dossier transmis', hint: 'Soumission sécurisée de votre analyse' },
  { id: 'review', label: 'Revue & bilan', hint: 'Notification par email sous environ 72 h' },
  { id: 'reco', label: 'Recommandations publiées', hint: 'Plan d’actions et suivi conseiller' },
];

export default {
  name: 'ClientJourneyEmptyState',
  props: {
    title: { type: String, required: true },
    message: { type: String, required: true },
    /** Index 0–3 de l’étape courante dans la timeline. */
    activeStep: { type: Number, default: 1 },
    primaryTo: { type: String, default: '' },
    primaryLabel: { type: String, default: '' },
    secondaryTo: { type: String, default: '' },
    secondaryLabel: { type: String, default: '' },
  },
  computed: {
    steps() {
      return DEFAULT_STEPS;
    },
  },
};
</script>

<style scoped>
.client-journey-empty {
  width: 100%;
  max-width: 34rem;
  margin: 0 auto;
  text-align: center;
  padding: 1.75rem 1.25rem;
}

.client-journey-empty__title {
  margin: 0 0 0.5rem;
  font-family: var(--font-display);
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--brand-navy);
}

.client-journey-empty__msg {
  margin: 0 0 1.35rem;
  color: var(--client-text-muted, #475569);
  font-size: 0.95rem;
  line-height: 1.55;
}

.client-journey-empty__timeline {
  list-style: none;
  margin: 0 0 1.5rem;
  padding: 0;
  text-align: left;
}

.client-journey-empty__step {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  position: relative;
  padding-bottom: 1rem;
}

.client-journey-empty__step:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 0.85rem;
  top: 1.75rem;
  bottom: 0;
  width: 2px;
  background: var(--client-line, #e2e8f0);
}

.client-journey-empty__step.is-done:not(:last-child)::before {
  background: rgba(45, 134, 89, 0.45);
}

.client-journey-empty__marker {
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  background: var(--paper);
  color: var(--client-text-muted, #64748b);
  border: 2px solid var(--client-line, #e2e8f0);
  z-index: 1;
}

.client-journey-empty__step.is-done .client-journey-empty__marker {
  background: rgba(45, 134, 89, 0.12);
  border-color: rgba(45, 134, 89, 0.45);
  color: var(--success);
}

.client-journey-empty__step.is-current .client-journey-empty__marker {
  background: var(--brand-gold-soft);
  border-color: var(--brand-gold);
  color: var(--brand-navy);
}

.client-journey-empty__step-body {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding-top: 0.15rem;
}

.client-journey-empty__step-label {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--client-text, #334155);
}

.client-journey-empty__step.is-current .client-journey-empty__step-label {
  color: var(--brand-navy);
}

.client-journey-empty__step.is-upcoming .client-journey-empty__step-label {
  color: var(--client-text-muted, #94a3b8);
  opacity: 0.75;
}

.client-journey-empty__step-hint {
  font-size: 0.8rem;
  color: var(--client-text-muted, #64748b);
  line-height: 1.35;
}

.client-journey-empty__step.is-upcoming .client-journey-empty__step-hint {
  color: var(--client-text-muted, #94a3b8);
  opacity: 0.75;
}

.client-journey-empty__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: center;
}

.client-journey-empty__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.7rem 1.1rem;
  border-radius: var(--radius-pill);
  font-size: 0.92rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.client-journey-empty__btn--primary {
  background: var(--brand-navy);
  color: var(--paper);
  border: 1px solid var(--brand-navy);
}

.client-journey-empty__btn--primary:hover {
  background: var(--brand-navy-deep);
  border-color: var(--brand-navy-deep);
  color: var(--paper);
}

.client-journey-empty__btn--ghost {
  background: var(--paper);
  color: var(--brand-navy);
  border: 1px solid var(--client-line, var(--line));
}

.client-journey-empty__btn--ghost:hover {
  border-color: var(--brand-navy);
  color: var(--brand-navy);
}
</style>
